import {
  Bars3Icon,
  ArrowLeftIcon,
  XMarkIcon,
  BellIcon,
  EllipsisVerticalIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import React, { useState, useRef, useEffect } from 'react';

import { useAppShell } from '../AppShellContext';
import { BrandLogo } from '../BrandLogo';
import { useGlobalSearch } from './useGlobalSearch';

import type { HeaderProps } from './Header.types';

import { Avatar } from '@/components/ui/Avatar';
import { BottomSheetConnector } from '@/components/ui/BottomSheetConnector';
import { Button } from '@/components/ui/Button';
import { BottomSheet } from '@/components/ui/Modal';
import { NotificationDot } from '@/components/ui/NotificationDot';
import { SearchCommand, SearchTrigger } from '@/components/ui/SearchCommand';
import { useSearchCommand } from '@/hooks';
import { useBreakpoint } from '@/hooks';
import { LAYOUT_DIMENSIONS } from '@/types/layout';

/**
 * Header component with responsive variants for mobile, tablet, and desktop.
 *
 * Variants:
 * - **main**: Dashboard header with hamburger menu, logo, notifications, avatar
 * - **subpage**: Back button, dynamic title, context menu
 * - **action**: Close button, action title, primary action button
 * - **search**: Back button, search input, clear button
 *
 * @example
 * ```tsx
 * // Main header
 * <Header
 *   variant="main"
 *   appName="FitPro"
 *   user={{ name: 'Sarah M.' }}
 *   onMenuClick={() => openDrawer()}
 * />
 *
 * // Subpage header
 * <Header
 *   variant="subpage"
 *   title="Workout Details"
 *   onBackClick={() => navigate(-1)}
 * />
 * ```
 */
export function Header({
  variant = 'main',
  title,
  appName = 'Dō',
  user,
  showNotifications = true,
  notificationCount,
  onMenuClick,
  onBackClick,
  onCloseClick,
  primaryAction,
  contextMenuItems,
  onNotificationsClick,
  onUserClick,
  search,
  logo,
  className = '',
  notificationDropdown,
  userDropdown,
  hideBrandOnDesktop = false,
  showSearch = true,
}: HeaderProps): React.JSX.Element {
  const { sidebarBehavior, toggleMobileDrawer, toggleSidebar } = useAppShell();
  const { isBelow, isAbove, width } = useBreakpoint();

  const isBelowMd = isBelow('md');
  const isAboveLg = isAbove('lg');
  // Phone-width threshold for the context menu — matches the user/notification
  // dropdowns, which swap to a bottom sheet (with connector beam) below 480px.
  const isContextMenuMobile = width < 480;

  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [connectorAnimating, setConnectorAnimating] = useState(false);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  // Search command state and data
  const {
    isOpen: isSearchOpen,
    open: openSearch,
    close: closeSearch,
  } = useSearchCommand();
  const {
    results: searchResults,
    isLoading: isSearchLoading,
    search: performSearch,
  } = useGlobalSearch();

  // Close context menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        contextMenuRef.current &&
        !contextMenuRef.current.contains(event.target as Node)
      ) {
        setContextMenuOpen(false);
      }
    }

    // Dropdown (≥ 480px) only — the phone action sheet is portaled outside this
    // ref and manages its own dismissal (backdrop, swipe, escape), so an
    // outside-click handler here would fight it.
    if (contextMenuOpen && !isContextMenuMobile) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [contextMenuOpen, isContextMenuMobile]);

  const headerHeight = isBelowMd
    ? LAYOUT_DIMENSIONS.headerHeightMobile
    : LAYOUT_DIMENSIONS.headerHeightDesktop;

  // Handler for menu button - uses context or prop
  const handleMenuClick = () => {
    if (onMenuClick) {
      onMenuClick();
    } else if (sidebarBehavior === 'hidden') {
      toggleMobileDrawer();
    } else {
      toggleSidebar();
    }
  };

  // Open the subpage context menu. On phone widths, fire the connector beam
  // first, then open the bottom sheet ~80ms later so the beam reads as the sheet
  // rising from the trigger (mirrors the user/notification dropdowns).
  const handleContextMenuToggle = () => {
    if (!contextMenuOpen && isContextMenuMobile) {
      setConnectorAnimating(true);
      setTimeout(() => setConnectorAnimating(false), 400);
      setTimeout(() => setContextMenuOpen(true), 80);
    } else {
      setContextMenuOpen((prev) => !prev);
    }
  };

  // Render left section based on variant
  const renderLeftSection = () => {
    switch (variant) {
      case 'main':
        return (
          <div className="flex items-center gap-3">
            {/* Hamburger menu for mobile drawer - only visible when sidebar is hidden */}
            {sidebarBehavior === 'hidden' && (
              <button
                type="button"
                onClick={handleMenuClick}
                className="rounded-lg p-2 text-neutral-400 transition-colors duration-fast hover:bg-contrast-700 hover:text-neutral-50 focus-visible:outline-none focus-visible:ring-state-focus"
                aria-label="Open menu"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
            )}
            {/* Logo and app name - hidden on desktop when hideBrandOnDesktop is true */}
            {!(isAboveLg && hideBrandOnDesktop) && (
              <BrandLogo name={appName} mark={logo} />
            )}
          </div>
        );

      case 'subpage':
      case 'search':
        return (
          <button
            type="button"
            onClick={onBackClick}
            className="rounded-lg p-2 text-neutral-400 transition-colors duration-fast hover:bg-contrast-700 hover:text-neutral-50 focus-visible:outline-none focus-visible:ring-state-focus"
            aria-label="Go back"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
        );

      case 'action':
        return (
          <button
            type="button"
            onClick={onCloseClick}
            className="rounded-lg p-2 text-neutral-400 transition-colors duration-fast hover:bg-contrast-700 hover:text-neutral-50 focus-visible:outline-none focus-visible:ring-state-focus"
            aria-label="Close"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        );

      default:
        return null;
    }
  };

  // Render center section based on variant
  const renderCenterSection = () => {
    switch (variant) {
      case 'subpage':
      case 'action':
        return (
          <h1 className="flex-1 truncate text-center font-display text-body-xl font-semibold text-neutral-50">
            {title}
          </h1>
        );

      case 'search':
        return (
          <div className="flex flex-1 items-center gap-2 px-2">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-neutral-500" />
              <input
                type="text"
                value={search?.value ?? ''}
                onChange={(e) => search?.onChange(e.target.value)}
                placeholder={search?.placeholder ?? 'Search...'}
                className="w-full rounded-lg border border-stroke-default bg-contrast-700 py-2 pl-10 pr-4 text-neutral-50 placeholder-neutral-500 focus:border-primary-300 focus:outline-none focus-visible:ring-state-focus-inset"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Render right section based on variant
  const renderRightSection = () => {
    switch (variant) {
      case 'main':
        return (
          <div className="flex items-center gap-2">
            {/* Search trigger */}
            {showSearch && (
              <SearchTrigger onClick={openSearch} compact={isBelowMd} />
            )}

            {/* Notifications - custom dropdown or default button */}
            {showNotifications &&
              (notificationDropdown ? (
                notificationDropdown
              ) : (
                <button
                  type="button"
                  onClick={onNotificationsClick}
                  className="relative rounded-lg p-2 text-neutral-400 transition-colors duration-fast hover:bg-contrast-700 hover:text-neutral-50 focus-visible:outline-none focus-visible:ring-state-focus"
                  aria-label="Notifications"
                >
                  <BellIcon className="h-6 w-6" />
                  {notificationCount !== undefined && (
                    <NotificationDot
                      count={notificationCount}
                      className="absolute right-1 top-1"
                    />
                  )}
                </button>
              ))}

            {/* User avatar/dropdown - custom dropdown or default button */}
            {userDropdown ? (
              userDropdown
            ) : (
              <button
                type="button"
                onClick={onUserClick}
                className="flex items-center gap-2 rounded-lg p-1 transition-colors duration-fast hover:bg-contrast-700 focus-visible:outline-none focus-visible:ring-state-focus"
                aria-label="User menu"
              >
                <Avatar
                  name={user?.name ?? user?.username}
                  src={user?.avatar}
                  size="sm"
                  tone="accent"
                />
                {/* Show name on tablet/desktop when not using custom dropdown */}
                {!isBelowMd && user?.name && (
                  <span className="text-label-sm font-medium text-neutral-50">
                    {user.name}
                  </span>
                )}
                {/* Dropdown indicator on tablet/desktop */}
                {!isBelowMd && (
                  <svg
                    className="h-4 w-4 text-neutral-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                )}
              </button>
            )}
          </div>
        );

      case 'subpage':
        if (!contextMenuItems?.length) return null;
        return (
          <div className="relative" ref={contextMenuRef}>
            <button
              type="button"
              onClick={handleContextMenuToggle}
              className="rounded-lg p-2 text-neutral-400 transition-colors duration-fast hover:bg-contrast-700 hover:text-neutral-50 focus-visible:outline-none focus-visible:ring-state-focus"
              aria-label="More options"
              aria-expanded={contextMenuOpen}
            >
              <EllipsisVerticalIcon className="h-6 w-6" />
            </button>

            {/* Phone (< 480px): connector beam from the ⋯ to the rising sheet. */}
            {isContextMenuMobile && (
              <BottomSheetConnector
                isAnimating={connectorAnimating}
                rightOffset={34}
              />
            )}

            {/* Tablet/desktop (≥ 480px): dropdown popover. Anchored to the ⋯
                button, so its shrink-to-fit width is capped there: the items
                don't wrap, which lets the menu grow to its longest label. */}
            {contextMenuOpen && !isContextMenuMobile && (
              <div className="absolute right-0 top-full z-50 mt-1 min-w-[180px] rounded-lg border border-stroke-default bg-contrast-950 py-1 shadow-lift-popover">
                {contextMenuItems.map((item) => {
                  const danger = item.variant === 'danger';
                  return (
                    <React.Fragment key={item.id}>
                      {item.dividerBefore && (
                        <div className="my-1 border-t border-stroke-subtle" />
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          item.onClick();
                          setContextMenuOpen(false);
                        }}
                        className={`flex w-full items-center gap-3 whitespace-nowrap px-4 py-2 text-left text-label-sm transition-colors duration-fast ${
                          danger
                            ? 'text-error-400 hover:bg-error-wash'
                            : 'text-neutral-300 hover:bg-contrast-700 hover:text-neutral-50'
                        }`}
                      >
                        {item.icon && (
                          <span
                            className={`h-5 w-5 ${danger ? 'text-error-400' : 'text-neutral-500'}`}
                          >
                            {item.icon}
                          </span>
                        )}
                        {item.label}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
            )}

            {/* Phone (< 480px): action sheet — a top-anchored dropdown is a
                desktop idiom on touch, so surface the same actions in a sheet. */}
            <BottomSheet
              isOpen={contextMenuOpen && isContextMenuMobile}
              onClose={() => setContextMenuOpen(false)}
              height="auto"
              data-testid="header-context-sheet"
            >
              <div className="flex flex-col gap-1">
                {contextMenuItems.map((item) => {
                  const danger = item.variant === 'danger';
                  return (
                    <React.Fragment key={item.id}>
                      {item.dividerBefore && (
                        <div className="my-1 border-t border-stroke-subtle" />
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          item.onClick();
                          setContextMenuOpen(false);
                        }}
                        className={`flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-body-md transition-colors duration-fast ${
                          danger
                            ? 'text-error-400 hover:bg-error-wash'
                            : 'text-neutral-100 hover:bg-lift-3'
                        }`}
                      >
                        {item.icon && (
                          <span
                            className={`h-5 w-5 ${danger ? 'text-error-400' : 'text-neutral-400'}`}
                          >
                            {item.icon}
                          </span>
                        )}
                        {item.label}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
            </BottomSheet>
          </div>
        );

      case 'action':
        if (!primaryAction) return null;
        return (
          <Button
            variant="primary"
            size="sm"
            onClick={primaryAction.onClick}
            disabled={primaryAction.disabled}
            loading={primaryAction.loading}
          >
            {primaryAction.label}
          </Button>
        );

      case 'search':
        return (
          <button
            type="button"
            onClick={search?.onClear}
            className="rounded-lg p-2 text-neutral-400 transition-colors duration-fast hover:bg-contrast-700 hover:text-neutral-50 focus-visible:outline-none focus-visible:ring-state-focus"
            aria-label="Clear search"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div
        className={`flex h-full items-center justify-between px-4 sm:px-6 lg:px-8 ${className}`}
        style={{ height: headerHeight }}
      >
        {/* Left section */}
        <div className="flex items-center">{renderLeftSection()}</div>

        {/* Center section */}
        {renderCenterSection()}

        {/* Right section */}
        <div className="flex items-center">{renderRightSection()}</div>
      </div>

      {/* Global Search Command Palette */}
      <SearchCommand
        isOpen={isSearchOpen}
        onClose={closeSearch}
        results={searchResults}
        onSearch={performSearch}
        loading={isSearchLoading}
      />
    </>
  );
}

export default Header;
